import { Check, RotateCcw, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Scenario, ScoreResult } from "@/lib/powtoon-be/types";
import { interactionModeOf } from "@/lib/scenarios/providers/spec";
import { withCacheBust } from "@/lib/utils";
import { postToAncestors } from "./postToAncestors";

export function ScoreScreen({
  scenario,
  score,
  error,
  onRestart,
}: {
  scenario: Scenario;
  score: ScoreResult | null;
  error?: string | null;
  onRestart?: () => void;
}) {
  const handleClose = () => {
    postToAncestors({ event: "sessionClose" });
    try { window.close(); } catch { /* ignore */ }
  };
  const isRolePlay = interactionModeOf(scenario.content) === "role_play";
  const breakdownLabel = isRolePlay ? "Skill" : "Question";
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-6 py-10">
      <div className="flex w-full max-w-md flex-col items-center text-center space-y-6">
        <div className="h-24 w-24 rounded-3xl bg-accent border border-border overflow-hidden">
          {withCacheBust(scenario.thumb_url, scenario.modified) && <img src={withCacheBust(scenario.thumb_url, scenario.modified)} className="h-full w-full object-cover" alt="" />}
        </div>

        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-foreground">Session Complete!</h1>
          <p className="text-sm text-muted-foreground">
            {isRolePlay
              ? "Here's how your role play went, with feedback to help you grow."
              : "Here's how your answers did, with feedback to help you grow."}
          </p>
        </div>

        {score ? (
          <div className="flex w-full flex-col items-center gap-4">
            {typeof score.grade === "number" && (
              <div className="flex flex-col items-center">
                <span className="text-6xl font-bold leading-none text-foreground">
                  {score.grade}%
                </span>
              </div>
            )}
            {score.pass ? (
              <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-600">
                Target Achieved <Check className="h-4 w-4" />
              </div>
            ) : (
              <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm font-semibold text-muted-foreground">
                Keep Practicing <Sparkles className="h-4 w-4" />
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {score.pass
                ? "You've hit the benchmark for this session — great demonstration of your skills."
                : "You're making steady progress. Review your feedback below and take another run to sharpen your responses."}
            </p>

            {score.breakdown.length > 0 && (
              <div className="w-full space-y-2 rounded-2xl border border-border bg-card p-4 text-left">
                <h2 className="text-sm font-semibold text-foreground">Coaching Insights</h2>
                <ul className="space-y-3">
                  {score.breakdown.map((entry, i) => {
                    const passed = entry.score === "pass";
                    return (
                      <li key={entry.question_id || i} className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {breakdownLabel} {i + 1}
                          </span>
                          <span
                            className={
                              passed
                                ? "inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-600"
                                : "inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground"
                            }
                          >
                            {passed ? "Strong response" : "Opportunity to polish"}
                          </span>
                        </div>
                        {entry.comment && (
                          <p className="text-xs text-muted-foreground">{entry.comment}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-destructive">
            {error ?? "We couldn't retrieve your score."}
          </p>
        )}

        <div className="flex w-full max-w-xs flex-col gap-2">
          {onRestart && (
            <Button size="lg" className="w-full" onClick={onRestart}>
              <RotateCcw className="h-5 w-5" />
              Practice Again
            </Button>
          )}
          <Button size="lg" variant="secondary" className="w-full" onClick={handleClose}>
            <X className="h-5 w-5" />
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
